import * as React from 'react';
import './fields.scss';
import { Button } from '@mui/material';
import { fileToBase64 } from '../../../shared/utils';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import Typography from '../../Typography';

export interface ImageInputFieldProps {
    id?: string,
    name?: string,
    label?: string,
    value?: string,
    disabled?: boolean,
    maxSizeKb?: number,
    onChange: (imageBase64?: string) => void,
    /** When provided, called with the raw File instead of converting to base64 */
    onFileSelect?: (file: File) => void,
    onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>,
    className?: string,
}

export const ImageInputField = (props: ImageInputFieldProps) => {
    const {
        id,
        name,
        label,
        value,
        disabled,
        maxSizeKb = 500,
        onChange,
        onFileSelect,
        onKeyUp,
        className,
    } = props;

    const [imageError, setImageError] = React.useState<string|undefined>(undefined);

    const t = useTranslations();

    const onFileChange = React.useCallback((file?: File) => {
        if(!file) return;
        if(file.size > maxSizeKb*1000) {
            setImageError(t(TK.imageSizeMustBeLessThanKB).replace('{0}', maxSizeKb.toString()));
            return;
        }
        setImageError(undefined);
        if (onFileSelect) {
            onFileSelect(file);
        } else {
            fileToBase64(file).then(onChange);
        }
    }, [onChange, onFileSelect, maxSizeKb, t]);

    return (
        <div className='imageFieldContainer'>
            {label && <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block', fontWeight: 500 }}>{label}</label>}
            { value && <>
                <img src={value} alt="Preview" />
                <Button
                    id={id ? `${id}-remove-button` : undefined}
                    style={{marginTop: 'var(--spacer-S)'}}
                    onClick={() => onChange(undefined)}
                    variant="outlined"
                    color="error"
                    size="small"
                >
                    {t(TK.remove)}
                </Button>
            </>}
            { !value && <>
                <input
                    id={id ? `${id}-input` : undefined}
                    className={className}
                    type='file'
                    name={name}
                    readOnly={disabled}
                    accept="image/*"
                    onChange={e => onFileChange(e.target.files && e.target.files[0] || undefined)}
                    onKeyUp={onKeyUp}
                />
                { imageError && <Typography size='smaller' style={{color: 'red'}}>{imageError}</Typography>}
            </>}
        </div>
    );
}
